import {
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SlideUpService } from '../services/slide-up.service';

@Directive({
  selector: '[utSlideUp]',
  standalone: false,
})
export class SlideUpDirective implements OnInit, OnDestroy {
  @Input() utSlideUp!: string; // Panel ID

  private destroy$ = new Subject<void>();
  private isOpen = false;
  private lastFocusedElement: HTMLElement | null = null;
  private lastFocusedInPanel: HTMLElement | null = null;
  private $analytics: any;
  private focusinListener: (() => void) | null = null;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private slideUpService: SlideUpService,
    @Inject('$analytics') $analytics: any,
  ) {
    this.$analytics = $analytics;
  }

  ngOnInit(): void {
    if (!this.utSlideUp) {
      console.error('SlideUpDirective: Panel ID is required');
      return;
    }

    // Subscribe to panel state changes
    this.slideUpService
      .getPanelState(this.utSlideUp)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        if (isOpen !== this.isOpen) {
          this.isOpen = isOpen;
          if (isOpen) {
            this.openPanel();
          } else {
            this.closePanel();
          }
        }
      });

    // Initially hide the panel
    this.renderer.setAttribute(this.elementRef.nativeElement, 'aria-hidden', 'true');
  }

  private openPanel(): void {
    const element = this.elementRef.nativeElement;

    // Store currently focused element to restore later
    this.lastFocusedElement = document.activeElement as HTMLElement;

    // Show panel
    this.renderer.setAttribute(element, 'aria-hidden', 'false');
    this.renderer.addClass(element, 'open');

    // Track analytics
    if (this.$analytics) {
      this.$analytics.eventTrack('SlideUp Opened', {
        category: 'SlideUp',
        label: this.utSlideUp,
      });
    }

    // Focus the close button after animation
    setTimeout(() => {
      this.focusCloseButton();
      this.setupFocusContainment();
    }, 100);
  }

  private closePanel(): void {
    const element = this.elementRef.nativeElement;

    // Hide panel
    this.renderer.setAttribute(element, 'aria-hidden', 'true');
    this.renderer.removeClass(element, 'open');

    // Track analytics
    if (this.$analytics) {
      this.$analytics.eventTrack('SlideUp Closed', {
        category: 'SlideUp',
        label: this.utSlideUp,
      });
    }

    // Remove focus containment
    this.removeFocusContainment();

    // Restore focus to the element that opened the panel
    if (this.lastFocusedElement) {
      setTimeout(() => {
        this.lastFocusedElement?.focus();
        this.lastFocusedElement = null;
      }, 50);
    }

    this.lastFocusedInPanel = null;
  }

  private focusCloseButton(): void {
    const element = this.elementRef.nativeElement;

    // Find and focus the close button
    const closeButton = element.querySelector('.slide-up-close') as HTMLElement;
    if (closeButton) {
      closeButton.focus();
      this.lastFocusedInPanel = closeButton;
    }
  }

  private setupFocusContainment(): void {
    const element = this.elementRef.nativeElement;

    // Listen for any focus changes in the document
    this.focusinListener = this.renderer.listen('document', 'focusin', (event: FocusEvent) => {
      if (!this.isOpen) return;

      const target = event.target as HTMLElement;

      // Check if the focused element is inside the panel
      if (!element.contains(target)) {
        // Focus escaped the panel - bring it back
        event.preventDefault();
        event.stopPropagation();

        // Return focus to the last focused element in the panel, or the close button
        if (this.lastFocusedInPanel && element.contains(this.lastFocusedInPanel)) {
          this.lastFocusedInPanel.focus();
        } else {
          this.focusCloseButton();
        }
      } else {
        // Update the last focused element within the panel
        this.lastFocusedInPanel = target;
      }
    });
  }

  private removeFocusContainment(): void {
    if (this.focusinListener) {
      this.focusinListener();
      this.focusinListener = null;
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) return;

    // Close on Escape key
    if (event.key === 'Escape') {
      event.preventDefault();
      this.slideUpService.close(this.utSlideUp);
      return;
    }

    // Handle Tab key for wrapping
    if (event.key === 'Tab') {
      this.handleTabWrapping(event);
    }
  }

  private handleTabWrapping(event: KeyboardEvent): void {
    const element = this.elementRef.nativeElement;

    // Find all focusable elements dynamically
    const focusableSelectors = [
      'a[href]:not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    const focusableElements = Array.from(
      element.querySelectorAll(focusableSelectors),
    ) as HTMLElement[];

    // Also include CodeMirror input fields
    const codeMirrorElements = element.querySelectorAll('.CodeMirror');
    codeMirrorElements.forEach((cm: any) => {
      if (cm.CodeMirror) {
        const inputField = cm.CodeMirror.getInputField();
        if (inputField && !focusableElements.includes(inputField)) {
          focusableElements.push(inputField);
        }
      }
    });

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement;

    if (event.shiftKey) {
      // Shift+Tab: wrap from first to last
      if (activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else if (activeElement === lastElement) {
      // Tab: wrap from last to first
      event.preventDefault();
      firstElement.focus();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;

    const target = event.target as HTMLElement;
    const element = this.elementRef.nativeElement;

    // Check if click is outside the panel
    if (!element.contains(target)) {
      // Check if click is on a trigger element (has data-slide-up-trigger attribute)
      const trigger = target.closest('[data-slide-up-trigger]');
      if (trigger) {
        const triggerId = trigger.getAttribute('data-slide-up-trigger');
        if (triggerId === this.utSlideUp) {
          // This is the trigger for this panel, don't close
          return;
        }
      }

      // Check if click is on editor or related elements (dropdowns, file inputs)
      if (
        target.closest('.uedit')
        || target.closest('.codeMirror-toolbar-dropdown')
        || (target instanceof HTMLInputElement && target.type === 'file')
      ) {
        return;
      }

      // Close panel on outside click
      this.slideUpService.close(this.utSlideUp);
    }
  }

  ngOnDestroy(): void {
    this.removeFocusContainment();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
